import { FetchAPI, Mutex } from './auth-service';
import AlertsService from './alerts-service';
import MetadataService from './metadata-service';
import { get as _get } from 'lodash';

class PolicyDataService {
    constructor () {
        if (!PolicyDataService.instance) {
            PolicyDataService.instance = this;
            this.namesMux = new Mutex();
        }
        this.searchPlaceholder = 'Search policies';

        const MC2CHECK = (process.env.BRICATA_APPLIANCE_TYPE === 'mc2');

        this.getPolicyTypes = () => {
            return FetchAPI({
                url: `${MC2CHECK ? 'mc2/policies' : 'sensors/groups'}/types`,
                method: 'GET',
            }).then(res => {
                const { btconf, datanode_system_config, ...rest } = res;
                return rest;
            });
        };

        this.getPolicies = (type) => {
            return FetchAPI({
                url: `${MC2CHECK ? 'mc2/policies' : 'sensors/groups'}/${type}`,
                method: 'GET',
            }).then(res => res || []);
        };

        this.getPoliciesWithTypes = (type) => {
            const keys = [];
            const policyPromises = [];
            return this.getPolicyTypes().then(res => {
                Object.keys(res).forEach((k, i) => {
                    keys.push(res[k]);
                    policyPromises.push(this.getPolicies(res[k].type));
                });
            }).then(() => {
                const p = [];
                return Promise.all(policyPromises).then((r) => {
                    r.forEach((v, i) => {
                        p.push({
                            name: keys[i].name,
                            type: keys[i].type,
                            policies: v,
                        });
                    });
                    return p;
                });
            });
        };

        // get policy currently running on sensor
        // https://cmc/api/sensors/groups/sensor/suricata_rules/192.168.1.64
        this.getCurrentPolicy = (type, sensor, cmc) => {
            const prefix = MC2CHECK && cmc ? `mc2/cmcapi/${cmc.name}/` : '';
            return FetchAPI({
                method: 'GET',
                url: `${prefix}sensors/groups/sensor/${type}/${encodeURIComponent(sensor)}`
            });
        };

        this.getPolicy = (type, name) => {
            return FetchAPI({
                url: `${MC2CHECK ? 'mc2/policies' : 'sensors/groups'}/${type}/${name}`,
                method: 'GET',
            });
        };

        this.createPolicy = (type, name, color) => {
            return FetchAPI({
                url: `${MC2CHECK ? 'mc2/policies' : 'sensors/groups'}/${type}/${name}/`,
                method: 'POST',
                body: {
                    name: name,
                    color: color || '',
                    data: {},
                }
            });
        };

        this.updatePolicy = (type, name, data) => {
            return FetchAPI({
                url: `${MC2CHECK ? 'mc2/policies' : 'sensors/groups'}/${type}/${name}`,
                method: 'PUT',
                body: data,
            });
        };

        this.getBusy = type => {
            return FetchAPI({
                url: `${MC2CHECK ? 'mc2/policies' : 'sensors/groups'}/${type}/-busy`,
                method: 'GET',
            });
        };

        this.deletePolicy = (type, name) => {
            return FetchAPI({
                url: `${MC2CHECK ? 'mc2/policies' : 'sensors/groups'}/${type}/${name}`,
                method: 'DELETE',
            });
        };

        this.assignPolicy = (type, name, sensors) => {
            return FetchAPI({
                url: `${MC2CHECK ? 'mc2/policies' : 'sensors/groups'}/assign/${type}/${name}/`,
                method: 'PUT',
                body: Array.isArray(sensors) ? { sensors: sensors } : sensors,
            });
        };
        
        // Get global datanode inventory (MC2 only)
        this.getGlobalDatanodes = () => {
            if (!(process.env.BRICATA_APPLIANCE_TYPE === 'mc2')) {
                return Promise.resolve([]);
            }
            return FetchAPI({
                url: '/mc2/datanodes/',
                method: 'GET',
            }).then(res => Array.isArray(res) ? res : []);
        };
        
        this.getDatanodeAssignments = (type) => {
            return FetchAPI({
                url: `${MC2CHECK ? 'mc2/datanode-config' : 'datanodes/groups'}/assignments/${type}`,
                method: 'GET',
            }).then(res => {
                return (res && typeof res === 'object' && !Array.isArray(res)) ? res : {};
            });
        };

        this.getDatanodePolicies = (type) => {
            return FetchAPI({
                url: `${MC2CHECK ? 'mc2/datanode-config' : 'datanodes/groups'}/${type}`,
                method: 'GET',
            }).then(res => {
                return Array.isArray(res) ? res : [];
            });
        };

        this.getDatanodePolicy = (type, name) => {
            return FetchAPI({
                url: `${MC2CHECK ? 'mc2/datanode-config' : 'datanodes/groups'}/${type}/${name}`,
                method: 'GET',
            });
        };

        this.createDatanodePolicy = (type, name, color) => {
            return FetchAPI({
                url: `${MC2CHECK ? 'mc2/datanode-config' : 'datanodes/groups'}/${type}/${name}/`,
                method: 'POST',
                body: {
                    name: name,
                    color: color || '',
                    data: {},
                }
            });
        };

        this.updateDatanodePolicy = (type, name, data) => {
            return FetchAPI({
                url: `${MC2CHECK ? 'mc2/datanode-config' : 'datanodes/groups'}/${type}/${name}`,
                method: 'PUT',
                body: data,
            });
        };

        this.deleteDatanodePolicy = (type, name) => {
            return FetchAPI({
                url: `${MC2CHECK ? 'mc2/datanode-config' : 'datanodes/groups'}/${type}/${name}`,
                method: 'DELETE',
            });
        };

        this.assignDatanodePolicy = (type, name, datanodes) => {
            return FetchAPI({
                url: `${MC2CHECK ? 'mc2/datanode-config' : 'datanodes/groups'}/assign/${type}/${name}/`,
                method: 'PUT',
                body: Array.isArray(datanodes) ? { datanodes } : datanodes,
            });
        };

        this.unassignDatanodePolicy = (type, name, datanodes) => {
            return FetchAPI({
                url: `${MC2CHECK ? 'mc2/datanode-config' : 'datanodes/groups'}/assign/${type}/${name}/`,
                method: 'DELETE',
                body: Array.isArray(datanodes) ? { datanodes } : datanodes,
            });
        };

        this.assignAllPolicy = (type, name) => {
            return FetchAPI({
                url: `${MC2CHECK ? 'mc2/policies' : 'sensors/groups'}/assignall/${type}/${name}`,
                method: 'PUT',
            });
        };

        this.verifyFileHash = (name, url) => {
            return FetchAPI({
                url: `${MC2CHECK ? 'mc2/policies' : 'sensors/groups'}/verify/file_hash_config/${name}`,
                method: 'PUT',
                body: {
                    port: '',
                    scheme: 'http',
                    url: url,
                }
            });
        };

        // "serialize" gator-json to gator
        this.toGator = filter => {
            return FetchAPI({
                url: `${MC2CHECK ? 'mc2' : 'sensors'}/gators/togator`,
                method: 'POST',
                body: filter,
            }).catch(err => {
                console.log(filter, 'is not a valid gator', err);
                return '';
            });
        };

        // "serialize" recursive alert/mtdt filter to gator
        this.filterToGator = filter => {
            if (_get(filter, 'nodes', []).length < 1 && (filter.variable || '') === '') return '';

            const gatorJSON = translateTree(filter);
            return this.toGator(gatorJSON);

            function translateTree (f) {
                if (f.variable) return translateNode(f);
                return {
                    operator: (f.operator || 'and').toUpperCase(),
                    nodes: (f.nodes || [])
                        .filter(n => translateTree(n))
                        .map(n => translateTree(n))
                };
            }

            function translateNode (f) {
                const t = getGatorType(f.type);
                const field = getGatorField(f.variable);
                if (!field) return undefined;

                const ret = {
                    type: t,
                    gator: { field, value: f.value }
                };
                return ret;
            }

            function getGatorType (t) {
                switch (t) {
                    case 'ip': return 'gator.gatorIP';
                    case 'ips': return 'gator.gatorIP';
                    case 'sensor_uuid': return 'gator.gatorString';
                    case 'int': return 'gator.gatorInt';
                    case 'number': return 'gator.gatorInt';
                    case 'port': return 'gator.gatorInt';
                    case 'pcr': return 'gator.gatorPCRE';
                    default: {
                        return 'gator.gatorString';
                    }
                }
            }

            // good times
            function getGatorField (v) {
                // skip conditions
                const mtdt = MetadataService.columnsMap[v];
                if (mtdt && mtdt.gator === 'none') return '';
                const alert = AlertsService.columnsMap[v];
                if (alert && alert.gator === 'none') return '';

                // generic mtdt handling
                if (v.startsWith('bro_log.')) return v.replace('bro_log.', 'bro_log:');

                // generic alert handling
                if (alert) return alert.gator || alert.variable;

                // unknown var, just return as is
                return v;
            }
        };

        this.getSavedSearches = () => {
            try {
                let searches = localStorage.getItem('savedSearches_policy_select') || '[]';
                if (searches.length < 1) searches = '[]';
                return JSON.parse(searches);
            } catch (err) {
                console.log('getSavedSearches error', err);
                return [];
            }
        };

        this.saveSearch = (str) => {
            if (!str || !str.length) return;
            const searches = this.getSavedSearches();
            const i = searches.findIndex(s => s === str);
            if (i >= 0) searches.splice(i, 1);
            searches.unshift(str);
            searches.splice(5);
            localStorage.setItem('savedSearches_policy_select', JSON.stringify(searches));
        };

        return PolicyDataService.instance;
    }
}

const PoliciesService = new PolicyDataService();

export {
    PoliciesService as default,
};
